import { FullStatsReport } from '@yandex-360/telemost-sdk/models/stats';
import { multicastChannel, MulticastChannel } from 'redux-saga';
import { all, call, delay, fork, put, take, takeLatest } from 'typed-redux-saga';

import { ChannelHolder } from '../../../helpers/channelHolder';
import { DrawingPerformanceStats, DrawingPerformanceStatsReport } from '../../../helpers/drawing-performance-stats';
import { createLogger } from '../../../helpers/loggerFacade';
import { multicastTakeEvery } from '../../../helpers/redux-saga';
import { cloudApi } from '../../../helpers/transport';
import { Connection } from '../Connection';
import { setDebugMenuStatsReport } from '../dtmf/debug-menu/actions';
import { SignalingAPI } from '../SignalingAPI';
import { Description, RtcEvent, SDKAdapter } from '../types';

const logger = createLogger('room-agent-stats-saga');

function* debugMenuStatsSaga(
    rtcStatsChannel: MulticastChannel<FullStatsReport>,
    drawingPerformanceStatsChannel: MulticastChannel<DrawingPerformanceStatsReport>
) {
    while (true) {
        const { rtcStats, drawingPerformanceStats } = yield* all({
            rtcStats: take(rtcStatsChannel, '*'),
            drawingPerformanceStats: take(drawingPerformanceStatsChannel, '*'),
            throttle: delay(5000),
        });

        yield* put(setDebugMenuStatsReport({ rtc: rtcStats.standard, drawingPerformance: drawingPerformanceStats }));
    }
}

function* cloudApiStatsSaga(rtcStatsChannel: MulticastChannel<FullStatsReport>, roomId?: string, peerId?: string) {
    if (!roomId || !peerId) {
        logger.warn("empty roomId or peerId. WebRTC stats won't be sent", {
            method: 'cloudApiStatsSaga',
        });
        return;
    }

    while (true) {
        const { rtcStats } = yield* all({ rtcStats: take(rtcStatsChannel, '*'), throttle: delay(10000) });
        try {
            yield* call([cloudApi, cloudApi.logStats], roomId, `room-agent@${peerId}`, rtcStats);
        } catch (error) {
            logger.error(error, { method: 'cloudApiStatsSaga' });
        }
    }
}

function* signalingStatsSaga(
    rtcStatsChannel: MulticastChannel<FullStatsReport>,
    drawingPerformanceStatsChannel: MulticastChannel<DrawingPerformanceStatsReport>,
    signaling: SignalingAPI
) {
    while (true) {
        const { rtcStats, drawingPerformanceStats } = yield* all({
            rtcStats: take(rtcStatsChannel, '*'),
            drawingPerformanceStats: take(drawingPerformanceStatsChannel, '*'),
            throttle: delay(10000),
        });

        const aggregatedStats = {
            outboundVideoStreams: rtcStats.standard.other_outbound_rtp_video_streams,
            ...drawingPerformanceStats,
        };

        yield* call([signaling, signaling.onStats], aggregatedStats);
    }
}

export function* statsSaga({
    adapter,
    connection,
    signaling,
    roomId,
    peerId,
    channelHolder,
    onRtcEventChannel,
    onIncomingSdpChannel,
    onOutgoingSdpChannel,
}: {
    adapter: SDKAdapter;
    connection: Connection;
    signaling: SignalingAPI;
    roomId?: string;
    peerId?: string;
    channelHolder: ChannelHolder;
    onRtcEventChannel: MulticastChannel<RtcEvent>;
    onIncomingSdpChannel: MulticastChannel<Description>;
    onOutgoingSdpChannel: MulticastChannel<Description>;
}) {
    const drawingPerformanceStatsCollector = new DrawingPerformanceStats({ interval: 5000 });
    const drawingPerformanceStatsChannel = multicastChannel<DrawingPerformanceStatsReport>();
    const rtcStatsChannel = multicastChannel<FullStatsReport>();

    yield* fork(debugMenuStatsSaga, rtcStatsChannel, drawingPerformanceStatsChannel);
    yield* fork(cloudApiStatsSaga, rtcStatsChannel, roomId, peerId);
    yield* fork(signalingStatsSaga, rtcStatsChannel, drawingPerformanceStatsChannel, signaling);

    yield* channelHolder.takeLatest(connection.onStats, function* (report) {
        yield* put(rtcStatsChannel, report);
    });
    yield* takeLatest(
        channelHolder.getChannelForEmmiter<DrawingPerformanceStatsReport>(
            drawingPerformanceStatsCollector,
            'performance-stats'
        ),
        function* (report: [DrawingPerformanceStatsReport]) {
            yield* put(drawingPerformanceStatsChannel, report[0]);
        }
    );

    function* onIceCandidateEventSaga(event: RTCPeerConnectionIceEvent) {
        if (event.candidate) {
            yield* call([adapter, adapter.onIceCandidate], event.candidate);
        }
    }
    yield* channelHolder.takeEvery(connection.onIceCandidate, onIceCandidateEventSaga);

    function* onRawStatsSaga(rawStats: RTCStatsReport) {
        yield* call([adapter, adapter.onRtcStats], rawStats);
    }
    yield* channelHolder.takeEvery(connection.onRawStats, onRawStatsSaga);

    function* onRtcEventSaga(event: RtcEvent) {
        if (event.type === 'connection-state' && event.kind === 'rtc') {
            yield* call([adapter, adapter.onConnectionState], event.value);
        }
        if (event.type === 'signaling-state') {
            yield* call([adapter, adapter.onSignalingState], event.value);
        }

        yield* call([signaling, signaling.onRtcEvent], event);
    }
    yield* multicastTakeEvery(onRtcEventChannel, onRtcEventSaga);

    function* onSdpSaga({ type, sdp }: Description) {
        yield* call([adapter, adapter.onSdp], { type, sdp });
    }
    yield* multicastTakeEvery(onIncomingSdpChannel, onSdpSaga);
    yield* multicastTakeEvery(onOutgoingSdpChannel, onSdpSaga);
}
